import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { FiKey, FiEye, FiEyeOff, FiCheck, FiX } from 'react-icons/fi';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import { Input } from '@/components/ui/Input';
import { 
  changePasswordService, 
  ChangePasswordInfoDto 
} from '@/services/changePasswordService';
import { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { toast } from 'sonner';
import { validatePassword } from '@/common/utils/commonUtil';
import { toastUtils } from '@/common/utils/toastUtils';

interface PasswordChangeStepProps {
  userInfo: ChangePasswordInfoDto;
  authResult: InisisAuthResult;
  urlToken: string;
  onSuccess: () => void;
}

/**
 * 비밀번호 유효성 검증 결과
 */
interface PasswordValidation {
  minLength: boolean;
  hasLetter: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  validTypesCount: number;
  isValid: boolean;
}

/**
 * 비밀번호 변경 단계 컴포넌트
 */
const PasswordChangeStep: React.FC<PasswordChangeStepProps> = ({
  userInfo,
  authResult,
  urlToken,
  onSuccess,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * 비밀번호 유효성 검증
   * validatePassword와 동일한 로직: 8자 이상 + 영문/숫자/특수문자 중 2가지 이상 조합
   */
  const passwordValidation = useMemo((): PasswordValidation => {
    const minLength = newPassword.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
    
    const validTypesCount = [hasLetter, hasNumber, hasSpecialChar].filter(Boolean).length;
    const isValid = validatePassword(newPassword) === null;
    
    return {
      minLength,
      hasLetter,
      hasNumber,
      hasSpecialChar,
      validTypesCount,
      isValid,
    };
  }, [newPassword]);

  /**
   * 비밀번호 일치 여부
   */
  const passwordMatch = useMemo(() => {
    if (!confirmPassword) return null;
    return newPassword === confirmPassword;
  }, [newPassword, confirmPassword]);

  /**
   * 제출 가능 여부
   */
  const canSubmit = passwordValidation.isValid && passwordMatch === true;

  /**
   * 비밀번호 변경 요청
   */
  const handleSubmit = async () => {
    if (!canSubmit || isLoading) return;

    setIsLoading(true);

    try {
      const result = await changePasswordService.changePassword({
        newPassword,
        userCi: authResult.userCi,
        userDi: authResult.userDi,
        token: authResult.token,
        changePasswordToken: urlToken,
      });

      if (result && result.success) {
        toastUtils.success('비밀번호가 성공적으로 변경되었습니다.');
        onSuccess();
      } else {
        toastUtils.error('비밀번호 변경에 실패했습니다. 다시 시도해 주세요.');
      }
    } catch (error) {
      console.error('비밀번호 변경 실패:', error);
      toastUtils.error('비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <IconWrapper>
          <FiKey size={24} />
        </IconWrapper>
        <Title>비밀번호 변경</Title>
        <Description>새로운 비밀번호를 설정해 주세요.</Description>
      </Header>

      <LoginIdDisplay>
        <LoginIdLabel>아이디</LoginIdLabel>
        <LoginIdValue>{userInfo.loginId}</LoginIdValue>
      </LoginIdDisplay>

      <FormContainer>
        <FormGroup>
          <Label>새 비밀번호</Label>
          <PasswordInputWrapper>
            <Input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="새 비밀번호를 입력해 주세요."
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <PasswordToggle 
              type="button" 
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </PasswordToggle>
          </PasswordInputWrapper>
          
          {newPassword && (
            <ValidationList>
              <ValidationItem $isValid={passwordValidation.minLength}>
                {passwordValidation.minLength ? <FiCheck size={14} /> : <FiX size={14} />}
                <span>8자 이상</span>
              </ValidationItem>
              <ValidationItem $isValid={passwordValidation.validTypesCount >= 2}>
                {passwordValidation.validTypesCount >= 2 ? <FiCheck size={14} /> : <FiX size={14} />}
                <span>영문, 숫자, 특수문자 중 2가지 이상 조합</span>
              </ValidationItem>
            </ValidationList>
          )}
        </FormGroup>

        <FormGroup>
          <Label>새 비밀번호 확인</Label>
          <PasswordInputWrapper>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="비밀번호를 다시 입력해 주세요."
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  handleSubmit();
                }
              }}
            />
            <PasswordToggle 
              type="button" 
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </PasswordToggle>
          </PasswordInputWrapper>
          
          {confirmPassword && (
            <MatchMessage $isMatch={passwordMatch}>
              {passwordMatch ? (
                <>
                  <FiCheck size={14} />
                  <span>비밀번호가 일치합니다</span>
                </>
              ) : (
                <>
                  <FiX size={14} />
                  <span>비밀번호가 일치하지 않습니다</span>
                </>
              )}
            </MatchMessage>
          )}
        </FormGroup>
      </FormContainer>

      <Button
        $variant="gradient"
        $size="md"
        $isFull
        disabled={!canSubmit || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? '변경 중...' : '비밀번호 변경'}
      </Button>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.2) 100%);
  color: #6366f1;
`;

const Title = styled.h2`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const Description = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

const LoginIdDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
  border: 1px solid ${semanticColors.border.primary};
`;

const LoginIdLabel = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.secondary};
`;

const LoginIdValue = styled.span`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
  font-weight: 600;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.primary};
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${semanticColors.text.primary};
  }
`;

const ValidationList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 4px;
`;

const ValidationItem = styled.div<{ $isValid: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  ${typography.caption.caption2}
  color: ${({ $isValid }) => $isValid ? '#10b981' : semanticColors.text.tertiary};
  
  svg {
    flex-shrink: 0;
  }
`;

const MatchMessage = styled.div<{ $isMatch: boolean | null }>`
  display: flex;
  align-items: center;
  gap: 6px;
  ${typography.caption.caption1.default}
  color: ${({ $isMatch }) => $isMatch ? '#10b981' : '#ef4444'};
  margin-top: 4px;
  
  svg {
    flex-shrink: 0;
  }
`;

export default PasswordChangeStep;
